// English source strings for platform operations (module 19, ADR-012).
export const enOps = {
  // ------------------------------------------------------------ API messages
  'api.opsJobStale': 'This job changed since you looked at it. Reload it and decide again.',
  'api.opsJobState': 'This action is not available while the job is {state}.',
  'v.opsReconcile': 'Confirm that you checked the external outcome before replaying this job.',
  'v.opsReasonShort': 'Give a reason of at least 3 characters.',

  // ------------------------------------------------------------ job states and effects
  'ops.state.queued': 'Queued',
  'ops.state.scheduled': 'Scheduled',
  'ops.state.retrying': 'Retrying',
  'ops.state.in_flight': 'In progress',
  'ops.state.dead_letter': 'Dead letter',
  'ops.state.resolved': 'Resolved manually',
  'ops.state.dispatched': 'Delivered',
  'ops.effect.external': 'External effect',
  'ops.effect.internal': 'Internal',
  'ops.effect.none': 'No subscriber',

  // ------------------------------------------------------------ workspace
  'ops.title': 'Platform operations',
  'ops.intro': 'Observe background work, recovery evidence and deployment decisions. Transport health is not business success.',
  'ops.tabsLabel': 'Platform operations sections',
  'ops.tab.jobs': 'Background jobs',
  'ops.tab.profile': 'Deployment profile',
  'ops.tab.recovery': 'Recovery & controls',
  'ops.m.dead': 'Dead letters',
  'ops.m.deadSub': 'Need an operator decision',
  'ops.m.retrying': 'Retrying',
  'ops.m.retryingSub': 'Failed at least once; retried automatically',
  'ops.m.waiting': 'Waiting',
  'ops.m.waitingSub': 'Queued, scheduled or in progress',
  'ops.m.oldest': 'Oldest due',
  'ops.m.oldestSub': 'Age of the oldest job waiting to run',
  'ops.seconds': '{count} s',
  'ops.minutes': '{count} min',
  'ops.hours': '{count} h',

  'ops.health.title': 'Service health',
  'ops.health.sub': 'States only; technical details stay in operator logs.',
  'ops.health.database': 'Database',
  'ops.health.schema': 'Schema',
  'ops.health.objects': 'Object storage',
  'ops.health.workers': 'Workers',
  'ops.health.ok': 'Available',
  'ops.health.unavailable': 'Unavailable',
  'ops.schema.current': 'Current',
  'ops.schema.pending': 'Migrations pending',
  'ops.schema.ahead': 'Newer than this release (rollback compatible)',
  'ops.schema.drift': 'Changed after release',
  'ops.schema.unknown': 'Unknown',
  'ops.workers.active': { one: '{count} active worker', other: '{count} active workers', zero: 'No active worker' },
  'ops.workers.lastSeen': 'Last heartbeat {time}',
  'ops.workers.never': 'No worker heartbeat recorded',

  'ops.jobs.title': 'Worker queue',
  'ops.jobs.sub': 'Jobs on your sites. Payload values are never shown.',
  'ops.filter.state': 'State',
  'ops.filter.allStates': 'All states',
  'ops.filter.site': 'Site',
  'ops.filter.allSites': 'All my sites',
  'ops.col.job': 'Job',
  'ops.col.site': 'Site',
  'ops.col.state': 'State',
  'ops.col.attempts': 'Attempts',
  'ops.col.when': 'Next attempt',
  'ops.col.action': 'Action',
  'ops.inspect': 'Inspect',
  'ops.empty': 'No jobs match these filters.',
  'ops.orgLevel': 'Organisation',
  'ops.loaded': { one: '{count} job loaded', other: '{count} jobs loaded' },
  'ops.tableLabel': 'Background jobs',

  'ops.job.title': 'Job details',
  'ops.job.sub': 'Read-only facts first; actions are recorded with your reason.',
  'ops.f.topic': 'Topic',
  'ops.f.effect': 'Side effect',
  'ops.f.state': 'State',
  'ops.f.attempts': 'Attempts',
  'ops.f.operatorRetries': 'Operator retries',
  'ops.f.created': 'Created',
  'ops.f.available': 'Next attempt',
  'ops.f.lease': 'Lease until',
  'ops.f.deadLettered': 'Dead-lettered',
  'ops.f.resolved': 'Resolved',
  'ops.f.lastError': 'Last error (redacted)',
  'ops.f.payloadKeys': 'Payload fields',
  'ops.f.correlation': 'Correlation reference',
  'ops.f.resolution': 'Resolution',
  'ops.history': 'Operator history',
  'ops.history.none': 'No operator actions yet.',
  'ops.act.retry': 'Retry',
  'ops.act.dead_letter': 'Move to dead letters',
  'ops.act.resolve': 'Mark resolved',
  'ops.reason': 'Reason',
  'ops.reasonHint': 'Recorded in the audit trail.',
  'ops.external.note': 'This job sends something outside Passage. A worker may have stopped after the effect happened; check the external outcome before replaying it.',
  'ops.external.check': 'I checked the external outcome: it did not happen, or repeating it is acceptable.',
  'ops.done.retry': 'The job is queued for another attempt.',
  'ops.done.dead_letter': 'The job was moved to dead letters.',
  'ops.done.resolve': 'The job was marked resolved.',
  'ops.noOperate': 'You can view this job but cannot act on it.',
  'ops.noActions': 'No action is available in this state.',

  // ------------------------------------------------------------ deployment profile
  'ops.profile.title': 'Deployment planning',
  'ops.profile.sub': 'Read-only facts. Hosting, residency and recovery targets are owner decisions.',
  'ops.profile.active': 'Active profile',
  'ops.profile.build': 'Build',
  'ops.profile.unversioned': 'Unversioned local build',
  'ops.profile.schema': 'Schema version',
  'ops.profile.region': 'Hosting region',
  'ops.profile.availability': 'Availability',
  'ops.profile.residency': 'Residency claim',
  'ops.profile.residencyNo': 'Not claimable: every data flow, including logs and backups, must be evidenced in the approved region.',
  'ops.profile.residencyYes': 'Evidenced for every data flow',
  'ops.profile.flows': 'Data flows of the active profile',
  'ops.profile.options': 'Deployment options',
  'ops.decisionRequired': 'Decision required',
  'ops.col.profile': 'Option',
  'ops.col.status': 'Status',
  'ops.col.decisions': 'Open decisions',
  'ops.col.flow': 'Data flow',
  'ops.col.location': 'Location',
  'ops.none': 'None',
  'ops.pid.local-production-like': 'Local production-like (synthetic data)',
  'ops.pid.managed-cloud': 'Scinops-managed cloud',
  'ops.pid.uae-cloud': 'UAE-hosted cloud',
  'ops.pid.private-cloud': 'Customer private cloud',
  'ops.pid.on-premises': 'On-premises',
  'ops.pstatus.rehearsed-locally': 'Rehearsed locally',
  'ops.pstatus.documented': 'Documented, not deployed',
  'ops.pstatus.not-started': 'Not started',
  'ops.flow.database': 'Database',
  'ops.flow.objects': 'Uploaded files',
  'ops.flow.backups': 'Backups',
  'ops.flow.logs': 'Logs',
  'ops.flow.email': 'Email delivery',
  'ops.flow.ai': 'AI assistance',
  'ops.fstatus.verified-locally': 'Verified locally',
  'ops.fstatus.undecided': 'Undecided',
  'ops.fstatus.unverified': 'Unverified',
  'ops.fstatus.disabled': 'Disabled',
  'ops.avail.single-instance': 'Single instance (no failover)',
  'ops.avail.undecided': 'Undecided',
  'ops.avail.not-designed': 'Not designed',
  'ops.service.title': 'Support and service profile',
  'ops.service.sub': 'No support terms are implied until an agreement exists.',
  'ops.service.edition': 'Edition',
  'ops.service.agreement': 'Agreement',
  'ops.service.contacts': 'Contacts',
  'ops.service.escalation': 'Escalation',
  'ops.service.responseTerms': 'Response terms',
  'ops.service.undecided': 'Undecided — no response time or 24/7 cover is implied',

  // ------------------------------------------------------------ recovery and controls
  'ops.recovery.title': 'Operational acceptance',
  'ops.recovery.sub': 'No recovery guarantees are asserted.',
  'ops.recovery.rpo': 'Recovery point objective (RPO)',
  'ops.recovery.rto': 'Recovery time objective (RTO)',
  'ops.recovery.retention': 'Backup retention',
  'ops.recovery.failover': 'Failover',
  'ops.failover.not-designed': 'Not designed',
  'ops.failover.designed': 'Designed, not rehearsed',
  'ops.failover.rehearsed': 'Rehearsed',
  'ops.runs.title': 'Backups and rehearsals',
  'ops.runs.sub': 'Measured runs. A backup without a verified restore is not recovery evidence.',
  'ops.runs.none': 'No runs recorded yet.',
  'ops.col.run': 'Run',
  'ops.col.finished': 'Finished',
  'ops.col.duration': 'Duration',
  'ops.col.outcome': 'Outcome',
  'ops.run.backup': 'Backup',
  'ops.run.restore_rehearsal': 'Restore rehearsal (verified)',
  'ops.run.upgrade_rehearsal': 'Upgrade and rollback rehearsal',
  'ops.run.predeploy_check': 'Pre-deployment check',
  'ops.outcome.succeeded': 'Succeeded',
  'ops.outcome.failed': 'Failed',
  'ops.durationMs': '{seconds} s',
  'ops.controls.title': 'Control mapping',
  'ops.controls.sub': 'Evidence-based, not a certification. A control without a supplied requirement reference or evidence is a gap.',
  'ops.col.control': 'Control',
  'ops.col.requirement': 'Requirement',
  'ops.col.evidence': 'Evidence',
  'ops.col.owner': 'Owner',
  'ops.requirementMissing': 'Reference to be supplied',
  'ops.ownerMissing': 'Unassigned',
  'ops.col.review': 'Review',
  'ops.reviewMissing': 'Not reviewed',
  'ops.reviewedBy': '{date} · {reviewer}',
  'ops.cstatus.evidenced-locally': 'Evidenced locally',
  'ops.cstatus.gap': 'Gap',
  'ops.control.tenant-isolation': 'Organisation data isolation',
  'ops.control.least-privilege-db-roles': 'Least-privilege database roles',
  'ops.control.authorization-default-deny': 'Default-deny authorisation',
  'ops.control.append-only-audit': 'Append-only audit trail',
  'ops.control.secrets-outside-images': 'Secrets kept out of images and code',
  'ops.control.log-redaction': 'Diagnostics without personal data or secrets',
  'ops.control.backup-restore-verified': 'Verified backup and restore',
  'ops.control.encryption-in-transit': 'Encryption in transit',
  'ops.control.encryption-at-rest-database': 'Database encryption at rest',
  'ops.control.malware-scanning': 'Malware scanning of uploads',
  'ops.control.data-residency': 'Data residency',
  'ops.control.incident-response': 'Incident response ownership',
  'ops.control.high-availability': 'High availability',
} as const;
